import { apiClient } from '@/api/client'

// ─── Types ────────────────────────────────────────────────────────────

export type CallDirection = 'inbound' | 'outbound'

/** ringing → ongoing → completed, or one of the terminal misses. */
export type CallStatus = 'ringing' | 'ongoing' | 'completed' | 'missed' | 'rejected' | 'failed'

export interface CallSummary {
  /** Meta's call id (`wacid.…`). Calls have no table of their own. */
  id: string
  /** DB id of the WABA phone the call is on — the `wabaId` in API paths. */
  waba_id: number
  direction: CallDirection
  status: CallStatus
  customer_id: number | null
  customer_name: string
  wa_id: string
  agent_id: number | null
  agent_name: string
  /** Business number called (inbound) — several numbers per company. */
  business_phone: string
  duration: number
}

/** Payload of a `call.*` event on the notification socket. Ring events
 *  carry the summary (+ `sdp` on `call.incoming`); `call.answer` /
 *  `call.status` carry only `{id, sdp | status}`. */
export type CallSocketPayload = Partial<Omit<CallSummary, 'status'>> & {
  id: string
  sdp?: string
  /** A summary status, or — on `call.status` — Meta's progress of an
   *  outgoing call: ringing / accepted (picked up) / rejected. */
  status?: CallStatus | 'accepted'
}

/** What Meta allows right now for one customer (cached server-side). */
export interface CallPermission {
  status: 'no_permission' | 'temporary' | 'permanent'
  /** ISO time a temporary permission ends (null for permanent / none). */
  expires_at: string | null
  can_call: boolean
  can_request: boolean
  /** When a hit limit frees up again (ISO), if Meta reported one. */
  call_limit_resets_at: string | null
  request_limit_resets_at: string | null
  /** Free-form request possible (customer wrote in the last 24h). */
  in_service_window: boolean
}

export interface CallingSettings {
  status: 'ENABLED' | 'DISABLED'
  call_icon_visibility: 'DEFAULT' | 'DISABLE_ALL'
  callback_permission_status: 'ENABLED' | 'DISABLED'
}

/** One row of a number's call history (a finished call). */
export interface CallHistoryItem {
  /** Meta's call id. */
  id: string
  customer_id: number | null
  customer_name: string
  wa_id: string
  direction: CallDirection
  status: CallStatus
  duration: number
  /** Agent who answered or placed it ('' when nobody did). */
  agent_name: string
  created_at: string
}

export type CallHistoryFilter = 'all' | 'missed' | 'incoming' | 'outgoing'

export interface CallHistoryPage {
  calls: CallHistoryItem[]
  /** Pass as `before` for the next (older) page; null on the last page. */
  next_before: string | null
}

type Envelope<T> = { status: boolean; data: T }

// ─── API Calls ────────────────────────────────────────────────────────

export const callsApi = {
  /** Newest-first call history of the number, keyset-paged by `before`. */
  list: (wabaId: string, params: { filter: CallHistoryFilter; before?: string | null }) =>
    apiClient
      .get<Envelope<CallHistoryPage>>(`/api/crm/${wabaId}/inbox/calls/`, {
        params: { filter: params.filter, before: params.before || undefined, page_size: 30 },
      })
      .then((r) => r.data.data),

  /** Place a business-initiated call with the browser's SDP offer. */
  start: (wabaId: string, customerId: number, sdp: string) =>
    apiClient
      .post<Envelope<CallSummary>>(`/api/crm/${wabaId}/inbox/calls/`, {
        customer_id: customerId,
        sdp,
      })
      .then((r) => r.data.data),

  /** Answer a ringing inbound call. 409 → another agent took it. */
  accept: (wabaId: string, callId: string, sdp: string) =>
    apiClient
      .post<Envelope<CallSummary>>(`/api/crm/${wabaId}/inbox/calls/accept/`, {
        call_id: callId,
        sdp,
      })
      .then((r) => r.data.data),

  reject: (wabaId: string, callId: string) =>
    apiClient
      .post(`/api/crm/${wabaId}/inbox/calls/reject/`, { call_id: callId })
      .then(() => undefined),

  terminate: (wabaId: string, callId: string) =>
    apiClient
      .post(`/api/crm/${wabaId}/inbox/calls/terminate/`, { call_id: callId })
      .then(() => undefined),

  getPermission: (wabaId: string, customerId: number) =>
    apiClient
      .get<Envelope<CallPermission>>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/call-permission/`,
      )
      .then((r) => r.data.data),

  /** Send the "allow calls" request message to the customer. */
  requestPermission: (wabaId: string, customerId: number) =>
    apiClient
      .post<Envelope<CallPermission>>(
        `/api/crm/${wabaId}/inbox/contacts/${customerId}/call-permission/`,
      )
      .then((r) => r.data.data),

  /** Is calling switched on for this number? (Shows the inbox call button.) */
  getStatus: (wabaId: string) =>
    apiClient
      .get<Envelope<{ enabled: boolean }>>(`/api/crm/${wabaId}/inbox/calling-status/`)
      .then((r) => r.data.data),

  getSettings: (wabaId: string) =>
    apiClient
      .get<Envelope<CallingSettings>>(`/api/crm/${wabaId}/inbox/calling-settings/`)
      .then((r) => r.data.data),

  updateSettings: (wabaId: string, data: Partial<CallingSettings>) =>
    apiClient
      .patch<Envelope<CallingSettings>>(`/api/crm/${wabaId}/inbox/calling-settings/`, data)
      .then((r) => r.data.data),
}
